import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Star, MapPin, Calendar, Clock, Fuel, Settings, Users, Shield,
    Award, Phone, MessageCircle, Heart, Share2, ChevronLeft, ChevronRight, Check
} from 'lucide-react'
import { useCartStore, useCurrencyStore, useLanguageStore, useSavedItemsStore } from '../../lib/store'
import { formatPrice, daysBetween } from '../../lib/utils'
import { vehiclesApi } from '../../lib/api'

export default function VehicleDetailPage() {
    const { id } = useParams()
    useTranslation()
    const { currency } = useCurrencyStore()
    const { language } = useLanguageStore()
    const { addItem, openCart } = useCartStore()
    const { isVehicleSaved, addVehicle, removeVehicle } = useSavedItemsStore()

    const today = new Date().toISOString().split('T')[0]
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0]
    const [currentImage, setCurrentImage] = useState(0)
    const [startDate, setStartDate] = useState(today)
    const [endDate, setEndDate] = useState(tomorrow)
    const [pickupLocation, setPickupLocation] = useState('owner_location')
    const [hotelName, setHotelName] = useState('')
    const [selectedAddons, setSelectedAddons] = useState<number[]>([])
    const [vehicleData, setVehicleData] = useState<any>(null)
    const [loading, setLoading] = useState(true)

    // Reviews state
    const [reviews, setReviews] = useState<any[]>([])
    const [reviewStats, setReviewStats] = useState({ average: 0, total: 0 })

    useEffect(() => {
        const fetchVehicle = async () => {
            if (!id) return
            setLoading(true)
            try {
                const response = await vehiclesApi.get(parseInt(id))
                if (response.data) {
                    setVehicleData(response.data)
                }

                // Fetch reviews from API
                const reviewsRes = await fetch(`/api/reviews/vehicle/${id}`)
                const reviewsData = await reviewsRes.json()
                if (reviewsData.success) {
                    setReviews(reviewsData.reviews || [])
                    setReviewStats(reviewsData.stats || { average: 0, total: 0 })
                }
            } catch (error) {
                console.error('Failed to fetch vehicle:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchVehicle()
    }, [id])

    // Show loading state
    if (loading && !vehicleData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <p className="text-gray-500">{language === 'en' ? 'Loading...' : 'Đang tải...'}</p>
            </div>
        )
    }

    // Use API data if available, otherwise use mock data
    const vehicle = vehicleData || {
        id: parseInt(id || '1'),
        ownerId: 5,
        type: 'motorcycle' as const,
        brand: 'Honda',
        model: 'Air Blade 160',
        year: 2023,
        licensePlate: '77B1-12345',
        color: 'Red/Black',
        seats: 2,
        transmission: 'Automatic',
        fuelType: 'Gasoline',
        dailyRate: 200000,
        hourlyRate: 30000,
        locationLat: 10.2899,
        locationLng: 103.9840,
        locationAddress: 'Dương Đông, Phú Quốc',
        status: 'active' as const,
        createdAt: Date.now() / 1000,
        updatedAt: Date.now() / 1000,
        translations: {
            en: {
                title: 'Honda Air Blade 160 2023',
                description: 'The Honda Air Blade 160 is a stylish and powerful scooter perfect for exploring Phú Quốc. With its smooth automatic transmission and fuel-efficient engine, you can ride comfortably all day long. Features include LED headlights, digital dashboard, and spacious under-seat storage.',
                features: ['LED Headlights', 'Digital Dashboard', 'USB Charging', 'ABS Brakes', 'Large Storage', 'Fuel Efficient'],
            },
            vi: {
                title: 'Honda Air Blade 160 2023',
                description: 'Honda Air Blade 160 là xe tay ga thời trang và mạnh mẽ, hoàn hảo để khám phá Phú Quốc. Với hộp số tự động mượt mà và động cơ tiết kiệm nhiên liệu, bạn có thể đi thoải mái cả ngày. Tính năng bao gồm đèn LED, bảng điều khiển kỹ thuật số và cốp xe rộng rãi.',
                features: ['Đèn LED', 'Bảng điều khiển số', 'Sạc USB', 'Phanh ABS', 'Cốp rộng', 'Tiết kiệm xăng'],
            },
        },
        media: [
            { id: 1, mediaType: 'image' as const, url: '/images/vehicles/airblade-1.jpg', thumbnailUrl: '/images/vehicles/airblade-1-thumb.jpg', displayOrder: 1 },
            { id: 2, mediaType: 'image' as const, url: '/images/vehicles/airblade-2.jpg', thumbnailUrl: '/images/vehicles/airblade-2-thumb.jpg', displayOrder: 2 },
            { id: 3, mediaType: 'image' as const, url: '/images/vehicles/airblade-3.jpg', thumbnailUrl: '/images/vehicles/airblade-3-thumb.jpg', displayOrder: 3 },
        ],
        addons: [
            { id: 1, addonType: 'fuel_full' as const, price: 50000, available: true, translations: { en: { name: 'Full Tank', description: 'Receive with full fuel tank' }, vi: { name: 'Bình xăng đầy', description: 'Nhận xe với bình xăng đầy' } } },
            { id: 2, addonType: 'helmet' as const, price: 0, available: true, translations: { en: { name: 'Helmet (Free)', description: '2 helmets included' }, vi: { name: 'Mũ bảo hiểm (Miễn phí)', description: '2 mũ bảo hiểm đi kèm' } } },
            { id: 3, addonType: 'gps' as const, price: 30000, available: true, translations: { en: { name: 'GPS Phone Holder', description: 'Phone holder with GPS mount' }, vi: { name: 'Giá đỡ điện thoại GPS', description: 'Giá đỡ điện thoại có GPS' } } },
            { id: 4, addonType: 'driver' as const, price: 300000, available: true, translations: { en: { name: 'Driver (Per Day)', description: 'Experienced local driver' }, vi: { name: 'Tài xế (Theo ngày)', description: 'Tài xế địa phương kinh nghiệm' } } },
        ],
        owner: {
            id: 5,
            fullName: 'Nguyễn Văn Minh',
            avatarUrl: '/images/avatars/owner1.jpg',
            phone: '+84 123 456 789',
            verified: true,
            cccdVerified: true,
            phoneVerified: true,
            faceVerified: true,
            rating: 4.9,
            reviewCount: 127,
            responseTime: '< 1 hour',
            memberSince: '2023',
        },
        rating: 4.8,
        reviewCount: 45,
    }

    const translation = vehicle.translations[language]
    const isSaved = isVehicleSaved(vehicle.id)

    const calculateDays = () => {
        if (!startDate || !endDate) return 0
        return daysBetween(new Date(startDate), new Date(endDate))
    }

    const calculateTotal = () => {
        const days = calculateDays()
        if (days === 0) return 0

        let total = vehicle.dailyRate * days
        selectedAddons.forEach((addonId) => {
            const addon = vehicle.addons.find((a: any) => a.id === addonId)
            if (addon && addon.addonType !== 'helmet') {
                total += addon.price * (addon.addonType === 'driver' ? days : 1)
            }
        })
        return total
    }

    const handleAddToCart = () => {
        if (!startDate || !endDate) {
            alert(language === 'en' ? 'Please select dates' : 'Vui lòng chọn ngày')
            return
        }

        addItem({
            vehicle: vehicle as any,
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            pickupLocation: pickupLocation === 'hotel' ? hotelName : vehicle.locationAddress,
            deliveryToHotel: pickupLocation === 'hotel',
            hotelName: pickupLocation === 'hotel' ? hotelName : undefined,
            addons: selectedAddons.map((id) => ({ addonId: id, included: true })),
        })
        openCart()
    }

    const toggleAddon = (addonId: number) => {
        setSelectedAddons((prev) =>
            prev.includes(addonId) ? prev.filter((id) => id !== addonId) : [...prev, addonId]
        )
    }

    const toggleSave = () => {
        if (isSaved) {
            removeVehicle(vehicle.id)
        } else {
            addVehicle(vehicle.id)
        }
    }


    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            {/* Image Gallery */}
            <div className="relative bg-black">
                <div className="aspect-[16/9] md:aspect-[21/9] bg-gray-800 flex items-center justify-center">
                    <div className="text-gray-400 text-lg">{language === 'en' ? 'Vehicle Image' : 'Hình xe'} {currentImage + 1}</div>
                </div>

                {/* Gallery Navigation */}
                <button
                    onClick={() => setCurrentImage((prev) => Math.max(0, prev - 1))}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                    onClick={() => setCurrentImage((prev) => Math.min(vehicle.media.length - 1, prev + 1))}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition"
                >
                    <ChevronRight className="w-6 h-6" />
                </button>

                {/* Thumbnails */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {vehicle.media.map((_: any, index: number) => (
                        <button
                            key={index}
                            onClick={() => setCurrentImage(index)}
                            className={`w-3 h-3 rounded-full transition ${currentImage === index ? 'bg-white' : 'bg-white/50'}`}
                        />
                    ))}
                </div>

                {/* Save & Share */}
                <div className="absolute top-4 right-4 flex gap-2">
                    <button
                        onClick={toggleSave}
                        className={`p-3 rounded-full transition ${isSaved ? 'bg-red-500 text-white' : 'bg-white/80 hover:bg-white'}`}
                    >
                        <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
                    </button>
                    <button className="p-3 bg-white/80 rounded-full hover:bg-white transition">
                        <Share2 className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <div className="container mx-auto px-4 -mt-8 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Title & Rating */}
                        <div className="card">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-bold mb-2">{translation.title}</h1>
                                    <div className="flex items-center gap-4 text-gray-600">
                                        <div className="flex items-center">
                                            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500 mr-1" />
                                            <span className="font-semibold">{reviewStats.total > 0 ? reviewStats.average.toFixed(1) : vehicle.rating}</span>
                                            <span className="ml-1">({reviewStats.total || vehicle.reviewCount} {language === 'en' ? 'reviews' : 'đánh giá'})</span>
                                        </div>
                                        <div className="flex items-center">
                                            <MapPin className="w-4 h-4 mr-1" />
                                            {vehicle.locationAddress}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-3xl font-bold text-primary-600">
                                        {formatPrice(vehicle.dailyRate, currency)}
                                    </p>
                                    <p className="text-gray-500">/{language === 'en' ? 'day' : 'ngày'}</p>
                                </div>
                            </div>

                            {/* Specs */}
                            <div className="grid grid-cols-4 gap-4 py-4 border-t border-b">
                                <div className="text-center">
                                    <Settings className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                    <p className="text-sm font-medium">{vehicle.transmission}</p>
                                </div>
                                <div className="text-center">
                                    <Fuel className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                    <p className="text-sm font-medium">{vehicle.fuelType}</p>
                                </div>
                                <div className="text-center">
                                    <Users className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                    <p className="text-sm font-medium">{vehicle.seats} {language === 'en' ? 'seats' : 'chỗ'}</p>
                                </div>
                                <div className="text-center">
                                    <Calendar className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                    <p className="text-sm font-medium">{vehicle.year}</p>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="pt-4">
                                <h3 className="font-bold mb-2">{language === 'en' ? 'Description' : 'Mô tả'}</h3>
                                <p className="text-gray-600">{translation.description}</p>
                            </div>

                            {/* Features */}
                            <div className="pt-4">
                                <h3 className="font-bold mb-2">{language === 'en' ? 'Features' : 'Tính năng'}</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {translation.features?.map((feature: string, index: number) => (
                                        <div key={index} className="flex items-center text-sm">
                                            <Check className="w-4 h-4 text-green-500 mr-2" />
                                            {feature}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Owner Card */}
                        <div className="card">
                            <h3 className="font-bold mb-4">{language === 'en' ? 'Owner' : 'Chủ xe'}</h3>
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center text-2xl">
                                    {vehicle.owner.fullName.charAt(0)}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="font-bold">{vehicle.owner.fullName}</h4>
                                        {vehicle.owner.verified && (
                                            <span className="badge badge-success text-xs flex items-center gap-1">
                                                <Shield className="w-3 h-3" />
                                                {language === 'en' ? 'Verified' : 'Đã xác thực'}
                                            </span>
                                        )}
                                        {vehicle.owner.rating >= 4.5 && (
                                            <span className="badge badge-warning text-xs flex items-center gap-1">
                                                <Award className="w-3 h-3" />
                                                {language === 'en' ? 'Top Choice' : 'Lựa chọn hàng đầu'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                        <span className="flex items-center">
                                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 mr-1" />
                                            {vehicle.owner.rating} ({vehicle.owner.reviewCount})
                                        </span>
                                        <span className="flex items-center">
                                            <Clock className="w-4 h-4 mr-1" />
                                            {vehicle.owner.responseTime}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button className="p-2 border rounded-lg hover:bg-gray-50">
                                        <Phone className="w-5 h-5" />
                                    </button>
                                    <button className="p-2 border rounded-lg hover:bg-gray-50">
                                        <MessageCircle className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Reviews */}
                        <div className="card">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold flex items-center gap-2">
                                    {language === 'en' ? 'Reviews' : 'Đánh giá'}
                                    {reviewStats.total > 0 && (
                                        <span className="text-sm font-normal text-gray-500">
                                            ({reviewStats.average.toFixed(1)} / 5)
                                        </span>
                                    )}
                                </h3>
                            </div>
                            {reviews.length > 0 ? (
                                <div className="space-y-4">
                                    {reviews.map((review) => {
                                        const reviewerName = review.user_name || review.userName || review.guest_name || 'Anonymous'
                                        const reviewDate = review.created_at ? new Date(review.created_at * 1000).toLocaleDateString() : (review.date || '')
                                        return (
                                            <div key={review.id} className="border-b pb-4 last:border-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm text-primary-600 font-bold">
                                                        {reviewerName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-sm">{reviewerName}</p>
                                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                                            <div className="flex">
                                                                {[...Array(5)].map((_, i) => (
                                                                    <Star
                                                                        key={i}
                                                                        className={`w-3 h-3 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                                                                    />
                                                                ))}
                                                            </div>
                                                            <span>{reviewDate}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <p className="text-gray-600 text-sm">{review.comment}</p>
                                            </div>
                                        )
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-400 text-sm">
                                    {language === 'en' ? 'No reviews yet' : 'Chưa có đánh giá'}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Booking Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="card sticky top-20">
                            <h3 className="font-bold text-lg mb-4">{language === 'en' ? 'Book This Vehicle' : 'Đặt Xe Này'}</h3>

                            {/* Date Selection */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        {language === 'en' ? 'Start Date' : 'Ngày bắt đầu'}
                                    </label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        className="input w-full"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        {language === 'en' ? 'End Date' : 'Ngày kết thúc'}
                                    </label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        min={startDate || new Date().toISOString().split('T')[0]}
                                        className="input w-full"
                                    />
                                </div>
                            </div>

                            {/* Pickup Location */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">
                                    {language === 'en' ? 'Pickup Location' : 'Địa điểm nhận xe'}
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="pickup"
                                            value="owner_location"
                                            checked={pickupLocation === 'owner_location'}
                                            onChange={(e) => setPickupLocation(e.target.value)}
                                            className="mr-3"
                                        />
                                        <div>
                                            <p className="font-medium text-sm">{language === 'en' ? "Owner's Location" : 'Địa điểm chủ xe'}</p>
                                            <p className="text-xs text-gray-500">{vehicle.locationAddress}</p>
                                        </div>
                                    </label>
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            name="pickup"
                                            value="hotel"
                                            checked={pickupLocation === 'hotel'}
                                            onChange={(e) => setPickupLocation(e.target.value)}
                                            className="mr-3"
                                        />
                                        <div>
                                            <p className="font-medium text-sm">{language === 'en' ? "Deliver to Hotel" : 'Giao xe tại khách sạn'}</p>
                                            <p className="text-xs text-gray-500">{language === 'en' ? 'Free delivery' : 'Miễn phí giao xe'}</p>
                                        </div>
                                    </label>
                                </div>
                                {pickupLocation === 'hotel' && (
                                    <input
                                        type="text"
                                        placeholder={language === 'en' ? 'Enter hotel name' : 'Nhập tên khách sạn'}
                                        value={hotelName}
                                        onChange={(e) => setHotelName(e.target.value)}
                                        className="input w-full mt-2"
                                    />
                                )}
                            </div>

                            {/* Add-ons */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">
                                    {language === 'en' ? 'Add-ons' : 'Dịch vụ thêm'}
                                </label>
                                <div className="space-y-2">
                                    {vehicle.addons.map((addon: any) => {
                                        const addonTranslation = addon.translations[language]
                                        const isSelected = selectedAddons.includes(addon.id)

                                        return (
                                            <label
                                                key={addon.id}
                                                className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition ${isSelected ? 'border-primary-500 bg-primary-50' : 'hover:bg-gray-50'
                                                    }`}
                                            >
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleAddon(addon.id)}
                                                        className="mr-3"
                                                    />
                                                    <div>
                                                        <p className="font-medium text-sm">{addonTranslation.name}</p>
                                                        <p className="text-xs text-gray-500">{addonTranslation.description}</p>
                                                    </div>
                                                </div>
                                                <span className={`text-sm font-semibold ${addon.price === 0 ? 'text-green-600' : 'text-primary-600'}`}>
                                                    {addon.price === 0 ? (language === 'en' ? 'FREE' : 'Miễn phí') : `+${formatPrice(addon.price, currency)}`}
                                                </span>
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Price Breakdown */}
                            {calculateDays() > 0 && (
                                <div className="border-t pt-4 mb-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>{formatPrice(vehicle.dailyRate, currency)} x {calculateDays()} {language === 'en' ? 'days' : 'ngày'}</span>
                                        <span>{formatPrice(vehicle.dailyRate * calculateDays(), currency)}</span>
                                    </div>
                                    {selectedAddons.map((addonId) => {
                                        const addon = vehicle.addons.find((a: any) => a.id === addonId)
                                        if (!addon || addon.price === 0) return null
                                        const addonTranslation = addon.translations[language]
                                        const addonTotal = addon.addonType === 'driver' ? addon.price * calculateDays() : addon.price
                                        return (
                                            <div key={addonId} className="flex justify-between text-sm">
                                                <span>{addonTranslation.name}</span>
                                                <span>{formatPrice(addonTotal, currency)}</span>
                                            </div>
                                        )
                                    })}
                                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                                        <span>{language === 'en' ? 'Total' : 'Tổng cộng'}</span>
                                        <span className="text-primary-600">
                                            {formatPrice(calculateTotal(), currency)}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Book Button */}
                            <button
                                onClick={handleAddToCart}
                                className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold text-lg transition"
                            >
                                {language === 'en' ? 'Add to Booking' : 'Thêm vào đặt xe'}
                            </button>

                            {/* Escrow Notice */}
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                                <p className="font-semibold mb-1">🛡️ {language === 'en' ? 'Secure Payment' : 'Thanh toán an toàn'}</p>
                                <p>{language === 'en' ? 'Your payment is held in escrow and only released to the owner after successful rental.' : 'Tiền của bạn được giữ trong escrow và chỉ chuyển cho chủ xe sau khi thuê thành công.'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
